import {
    suspensions,
    competencies,
    restrictions,
    breathalyserTestsForSite,
    groupCompetenciesByOrGroup
} from 'areas/competence/domain';
import CardConstants from '../constants';
import {
    isStringSet,
    isNotNullOrEmpty,
    isEmptyObject,
    isDefined
} from 'utilities/is';
import { DateTime } from 'luxon';

// Returns a list of division IDs that the card holder is suspended in
const divisionBlocks = suspensions =>
    suspensions
        .filter(s => isStringSet(s.divisionID))
        .map(s => s.divisionID);

// Returns a list of site IDs that the card holder is suspended in
const siteBlocks = suspensions =>
    suspensions
        .filter(s => isStringSet(s.siteID))
        .map(s => s.siteID);

// Predicate for national level suspensions
const nationalPredicate = s =>
(!isStringSet(s.divisionID) && !isStringSet(s.siteName) &&
    CardConstants.NATIONALLY_SUSPENDED_IDS.indexOf(s.reasonLookupID.toUpperCase()) < 0);

// Predicate for division level suspensions
const divisionPredicate = divisionID => s => s.divisionID === divisionID;

// Site level suspensions
const sitePredicate = siteID => s => String(s.siteID) === String(siteID);

// Returns true if the card has at least one suspension matching the predicate
const hasSuspension = (card, predicate) => card.suspensions.filter(predicate).length > 0;

// Returns a value indicating whether the card holder has a national level suspension
const isNationallySuspended = card => hasSuspension(card, nationalPredicate);

// Returns true if the card holder has a suspension in the given division
const isSuspendedByDivision = (card, divisionID) => hasSuspension(card, divisionPredicate(divisionID));

// Returns true if the card holder has a suspension in the given site
const isSuspendedBySite = (card, siteID) => hasSuspension(card, sitePredicate(siteID));

const expiryDate = (card) => card.expiryDate ? new Date(card.expiryDate).toISOString() : null;

// Returns true if the card expiry is set and before today
const hasExpired = (card) => card.expiryDate && DateTime.fromISO(card.expiryDate) < DateTime.now();

const processJobRoles = (card) => {
    const jobRolesArray = [];

    if (isStringSet(card.jobRoles)) {

        const jobRoles = card.jobRoles.split('\\n');

        for (let x = 0; x < jobRoles.length; x++) {
            let roleData = jobRoles[x].split('|');
            let roleName = roleData[0];
            let isSafetyCritical = roleData[1];
            let isInvalid = roleData[2] === 'Invalid';
            let isPrimaryJobRole = roleData[3] === 'Primary';
            let jobRoleID = roleData[4];
            let division = roleData[5];
            let isSuspended = roleData[6] === 'Suspended';
            let divisionID = roleData[7];
            let employerID = roleData[8];
            let employerName = roleData[9];

            let displayText = 
                isStringSet(employerName) 
                    ? `${employerName}-${roleName} (Employer)`
                    : `${roleName} (${division})`;

            let job = {
                name: roleName,
                id: jobRoleID,
                isSafetyCritical,
                isInvalid,
                isPrimaryJobRole,
                isSuspended,
                division,
                divisionID,
                employerID,
                employerName,
                displayName: displayText
            };

            jobRolesArray.push(job);
        }
    }

    return jobRolesArray;
};

const processEmployerData = (card) => {
    if (!card) return null;

    const employersArray = [];

    if (isStringSet(card.employer)) {
        employersArray.push({
            id: card.employerID,
            name: card.employer
        });
    }
    if (isStringSet(card.secondaryEmployers) && isStringSet(card.secondaryEmployerIDs)) {
        const employerNames = card.secondaryEmployers.split(',');
        const employerIds = card.secondaryEmployerIDs.split(',');

        for (let i = 0; i < employerNames.length; i++) {
            employersArray.push({
                id: employerIds[i],
                name: employerNames[i]
            });
        }
    }

    return employersArray;
};

const missingCompetences = (competences, groupFilter, missingFilter) => {
    const missing = [];

    const groupedCompetences = groupCompetenciesByOrGroup((competences || []).filter(groupFilter));

    Object.keys(groupedCompetences).forEach(key => {

        const competenceGroup = groupedCompetences[key];
        const isOrGroup = key !== "__ungrouped__";

        const missed = competenceGroup.filter(missingFilter);

        if ((missed.length > 0 && !isOrGroup) || (isOrGroup && missed.length === competenceGroup.length))
            missing.push(missed);
    });

    return missing;

};

const validateDivisionCompetences = (card, locationConfig, location) => {

    if (!(location && location.division))
        return [];

    const groupFilter = d => d.divisionID === location.division.divisionID;

    const competences = locationConfig.divisionCompetences || [];

    const missingFilter =
        (dc) => card.competencies.findIndex(cc => {
            if (String(cc.alias) === dc.alias) {
                if ((dc.divisionSpecific === false || dc.divisionID === cc.divisionID)
                    && (dc.companySpecific === false || dc.companyID === cc.companyID)) {
                    return true;
                }
            }
            if (dc.cscsCardSchemeID) {

                if (card.cscsCards) {
                    if (card.cscsCards.some(c => c.cscsCardSchemeID.toLowerCase() == dc.cscsCardSchemeID
                        && c.cscsCardSchemeName == dc.cscsCardSchemeName
                        && (c.cscsCardType == dc.cscsCardType || dc.cscsCardType === 'All Card Types')))
                        return true;
                }
            }

            return false;
        }) === -1;

    return missingCompetences(competences, groupFilter, missingFilter);
};

const validateProjectCompetences = (card, locationConfig, location) => {

    if (!(location && location.project))
        return [];

    const competences = (locationConfig.projectCompetences || []);
    const groupFilter = p => p.projectID === location.project.projectID;
    const missingFilter =
        pc => card.competencies.findIndex(cc => {
            if (String(cc.alias) === pc.alias) {
                if ((pc.projectSpecific === false || pc.projectID === cc.projectID)
                    && (pc.divisionSpecific === false || pc.divisionID === cc.divisionID)
                    && (pc.companySpecific === false || pc.companyID === cc.companyID)) {
                    return true;
                }
            }

            if (pc.cscsCardSchemeID) {

                if (card.cscsCards) {
                    if (card.cscsCards.some(c => c.cscsCardSchemeID.toLowerCase() == pc.cscsCardSchemeID
                        && c.cscsCardSchemeName == pc.cscsCardSchemeName
                        && (c.cscsCardType == pc.cscsCardType || pc.cscsCardType === 'All Card Types')))
                        return true;
                }
            }
            return false;

        }) === -1;

    return missingCompetences(competences, groupFilter, missingFilter);
};

const validateSiteCompetences = (card, locationConfig, location) => {
    if (!(location && location.site)) {
        return [];
    }

    const competences = locationConfig.siteCompetences || [];
    const groupFilter = site => site.siteID === location.site.siteID;
    const missingFilter =
        sc => card.competencies.findIndex(cc => {
            if (String(cc.alias) === sc.alias) {
                if ((sc.siteSpecific === false || sc.siteID === String(cc.siteID) || String(cc.siteCode) === "NSS")
                    && (sc.companySpecific === false || sc.companyID === cc.companyID)
                    && (sc.divisionSpecific === false || sc.divisionID === cc.divisionID)) {
                    return true;
                }
            }
            if (sc.cscsCardSchemeID) {
                if (card.cscsCards) {
                    if (card.cscsCards.some(c => c.cscsCardSchemeID.toLowerCase() == sc.cscsCardSchemeID
                        && c.cscsCardSchemeName == sc.cscsCardSchemeName
                        && (c.cscsCardType == sc.cscsCardType || sc.cscsCardType === 'All Card Types')))
                        return true;
                }
            }
            return false;

        }) === -1;

    return missingCompetences(competences, groupFilter, missingFilter);
};

const validateZoneCompetences = (card, locationConfig, location) => {
    if (!(location && location.zone)) {
        return [];
    }

    const competences = locationConfig.zoneCompetences || [];
    const groupFilter = zc => zc.zoneID === location.zone.zoneID && zc.siteID === location.site.siteID;
    const missingFilter =
        zc => card.competencies.findIndex(cc => {
            if (String(cc.alias) === zc.alias) {
                if ((zc.siteSpecific === false || zc.siteID === String(cc.siteID))
                    && (zc.zoneSpecific === false || zc.zoneID === String(cc.zoneID))
                    && (zc.companySpecific === false || zc.companyID === cc.companyID)) {
                    return true;
                }
            }

            if (zc.cscsCardSchemeID) {
                if (card.cscsCards) {
                    if (card.cscsCards.some(c => c.cscsCardSchemeID.toLowerCase() == zc.cscsCardSchemeID
                        && c.cscsCardSchemeName == zc.cscsCardSchemeName
                        && (c.cscsCardType == zc.cscsCardType || zc.cscsCardType === 'All Card Types')))
                        return true;
                }
            }
            return false;

        }) === -1;
    return missingCompetences(competences, groupFilter, missingFilter);
};

const cscsCards = (cardsDelimited) => {
    if (!cardsDelimited)
        return null;

    const cardsDelimitedArray = cardsDelimited.split('\\n');
    const cards = cardsDelimitedArray.map(c => {

        const localCards = c.split('|');
        if (isNotNullOrEmpty(localCards)) {
            return {
                cscsCardSchemeID: localCards[0],
                cscsCardSchemeName: localCards[1],
                cscsCardType: localCards[2]
            };
        }
    });
    return cards;
};

const validationMissingCompetences = (missingDivisionCompetences, missingProjectCompetences, missingSiteCompetences, missingZoneCompetences) => {

    const allCompetences = [missingDivisionCompetences, missingProjectCompetences, missingSiteCompetences, missingZoneCompetences].flat(2);
    const missing = {
        division: allCompetences.filter(x => x.divisionSpecific).length > 0,
        project: allCompetences.filter(x => x.projectSpecific).length > 0,
        site: allCompetences.filter(x => x.siteSpecific).length > 0,
        zone: allCompetences.filter(x => x.zoneSpecific).length > 0,
        card: allCompetences.filter(x => x.cscsCardSchemeID).length > 0,
        employer: allCompetences.filter(x => x.employerID != null && x.employerName != null).length > 0,
        national: allCompetences.filter(x => !x.cscsCardSchemeID
            && !(x.employerID != null && x.employerName != null)
            && !x.divisionSpecific
            && !x.projectSpecific
            && !x.siteSpecific
            && !x.zoneSpecific).length > 0
    };

    return missing;

};

export function processCard(sourceCard, location, locationConfig, systemConfig) {
    if (!sourceCard || isEmptyObject(sourceCard)) {
        return null;
    }

    let card = {
        ...sourceCard.cardData
    };

    if (isDefined(sourceCard.fatigueInformation)) {
        card.fatigueInformation = sourceCard.fatigueInformation;
    }

    card.jobRolesArray = processJobRoles(card);
    card.employersArray = processEmployerData(card);

    card.selectedEmployerID = card.employersArray.length >= 1 ? card.employersArray[0].id : card.selectedEmployerID;
    card.selectedJobRoleID = card.jobRolesArray.length >= 1 ? card.jobRolesArray[0].id : card.selectedJobRoleID;
    card.competencies = competencies(sourceCard.competenceData);
    card.suspensions = suspensions(sourceCard.competenceData);
    card.divisionBlocks = divisionBlocks(card.suspensions);
    card.siteBlocks = siteBlocks(card.suspensions);
    card.restrictions = restrictions(sourceCard.competenceData);
    card.breathalyserTestsForSite =
        location && location.site && isStringSet(location.site.siteID)
            ? breathalyserTestsForSite(sourceCard.competenceData, location.site.siteID)
            : [];

    card.serialNumber = sourceCard.serialNumber;
    card.photo = sourceCard.photo;
    card.cardRender = sourceCard.card;
    card.expiryDate = expiryDate(card);
    card.cscsCards = cscsCards(sourceCard.cardData.cscsCards);

    card.isExpired = hasExpired(card);
    card.isSuspended = isStringSet(card.suspendedDate);
    card.isCancelled = isStringSet(card.cancelledDate);

    card.isNationallySuspended = isNationallySuspended(card);
    card.isSuspendedByDivision = location && location.division && isSuspendedByDivision(card, location.division.divisionID);
    card.isSuspendedBySite = location && location.site && isStringSet(location.site.siteID) && isSuspendedBySite(card, location.site.siteID);

    card.hasEmployer = isStringSet(card.employer);
    card.hasSponsor = isStringSet(card.sponsor);
    card.hasBreathalyserTestForSite = location && location.site && breathalyserTestsForSite.length > 0;

    card.noEmployer = !card.hasEmployer;
    card.noSponsor = !card.hasSponsor && card.requiresSponsorship;

    card.isMinimalCardDisplayShowingBlocks =
        card.isSuspendedByDivision || card.isSuspendedBySite;

    card.isMinimalCardDisplay =
        card.isExpired
        || card.isNationallySuspended
        || card.isSuspended
        || card.isCancelled
        || card.noEmployer
        || card.noSponsor
        || card.isSubscriptionExpired
        || card.isSubscriptionPending;

    // Ensure the spot check flag is set for access controllers
    card.canPerformSpotCheck = card.canPerformSpotCheck || card.canSwipeCards;
    card.canLogin = card.canPerformSpotCheck || card.canSwipeCards;

    card.displayOnly =
        card.isCancelled || card.isExpired || card.isSuspended
        || card.isSubscriptionExpired || card.isSubscriptionPending
        || !card.hasEmployer || card.noSponsor
        || (!card.canSwipeCards && !card.canSelfSwipe && !card.canPerformSpotCheck)
        || card.isNationallySuspended || card.isSuspendedByDivision || card.isSuspendedBySite;

    card.limitedMenu = !card.isSuspended && !card.isCancelled && card.hasEmployer && !card.noSponsor && (card.canSelfSwipe || card.canPerformSpotCheck) && !card.canSwipeCards;
    card.fullMenu = !card.isSuspended && !card.isCancelled && card.hasEmployer && !card.noSponsor && !card.canSelfSwipe && !card.canPerformSpotCheck;

    if (!card.hasEmployer) {
        card.warningMessage = "No Employer";
    } else if (card.noSponsor) {
        card.warningMessage = "No Sponsor";
    } else if (card.isNationallySuspended) {
        card.warningMessage = "Suspended Nationally";
    } else if (card.isSuspendedByDivision) {
        card.warningMessage = `Suspended in current ${systemConfig.divisionLevelName.toLowerCase()}: ${location.division.divisionName}`;
    } else if (card.isSuspendedBySite) {
        card.warningMessage = `Suspended in current ${systemConfig.siteLevelName.toLowerCase()}: ${location.site.siteName}`;
    } else if (card.isSubscriptionExpired) {
        card.warningMessage = "Subscription Expired";
    } else if (card.isSubscriptionPending) {
        card.warningMessage = "Subscription Pending";
    } else if (card.isSuspended) {
        card.warningMessage = "Card Suspended";
    } else if (card.isCancelled) {
        card.warningMessage = "Card Cancelled";
    } else if (card.isExpired) {
        card.warningMessage = "Card Expired";
    }

    card.missingDivisionCompetences = validateDivisionCompetences(card, locationConfig, location);
    card.missingProjectCompetences = validateProjectCompetences(card, locationConfig, location);
    card.missingSiteCompetences = validateSiteCompetences(card, locationConfig, location);
    card.missingZoneCompetences = validateZoneCompetences(card, locationConfig, location);

    card.missingMultipleCompetences = validationMissingCompetences(card.missingDivisionCompetences, card.missingProjectCompetences, card.missingSiteCompetences, card.missingZoneCompetences);
    card.missingCompetences = false;

    if (isNotNullOrEmpty(card.missingDivisionCompetences)
        || isNotNullOrEmpty(card.missingProjectCompetences)
        || isNotNullOrEmpty(card.missingSiteCompetences)
        || isNotNullOrEmpty(card.missingZoneCompetences)) {

        card.missingCompetences = true;
    }

    return card;
}